import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Globe, Play, Pause, Volume2, X, ZoomIn, Download, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useNostrProfile, useNostrEvent, findProfileByName } from '../hooks/useNostrQuery';
import TokenCard from './TokenCard';

interface NoteContentProps {
  content: string;
  tags?: string[][];
  /** When true, tall content is collapsed behind a "Show more" toggle. */
  collapsible?: boolean;
  /** Collapsed height in px (default 480). Ignored unless `collapsible`. */
  collapseHeight?: number;
  /** Tailwind `from-*` class for the fade overlay, matched to the surface the
   *  note sits on (default `from-background`; use `from-card` inside cards). */
  fadeFromClass?: string;
}

export default function NoteContent({ content, tags = [], collapsible = false, collapseHeight = 480, fadeFromClass = 'from-background' }: NoteContentProps) {
  const parts = parseContent(content, tags);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  // ── Collapse tall content behind a "Show more" toggle ──────────────
  const collapseRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    if (!collapsible) return;
    const el = collapseRef.current;
    if (!el) return;
    const measure = () => {
      // Compare the natural content height against the collapse threshold.
      setOverflowing(el.scrollHeight > collapseHeight + 24);
    };
    measure();
    // Re-measure when media/images finish loading and change the height.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsible, collapseHeight, content]);

  const isCollapsed = collapsible && overflowing && !expanded;

  // Collect image URLs for lightbox gallery navigation
  const imageUrls = parts.filter(p => p.type === 'image').map(p => p.value);

  // Collect non-image/video URLs for preview cards (normalized with protocol)
  const urlsForPreview = parts
    .filter(p => p.type === 'url')
    .map(p => p.value.match(/^https?:\/\//i) ? p.value : 'https://' + p.value)
    .slice(0, 2); // Max 2 previews

  // ── Group consecutive images for carousel rendering ──
  // If 2+ images appear in a row, render them as a swipeable carousel
  // instead of stacking vertically. Single images render normally.
  //
  // IMPORTANT: Nostr posts typically have newlines between image URLs,
  // which the parser splits into text parts containing just "\n".
  // We treat whitespace-only text parts as transparent so they don't
  // break image grouping. The whitespace is absorbed into the carousel.
  const groupedParts = useMemo(() => {
    const groups: (ContentPart | { type: 'image-carousel'; images: string[] })[] = [];
    let pendingImages: string[] = [];
    let pendingWhitespace: ContentPart[] = []; // whitespace between images

    const flushImages = () => {
      if (pendingImages.length >= 2) {
        groups.push({ type: 'image-carousel', images: [...pendingImages] });
      } else if (pendingImages.length === 1) {
        // Single image — also push back any absorbed whitespace before it
        for (const ws of pendingWhitespace) groups.push(ws);
        groups.push({ type: 'image', value: pendingImages[0] } as ContentPart);
      } else {
        // No images — push back any absorbed whitespace
        for (const ws of pendingWhitespace) groups.push(ws);
      }
      pendingImages = [];
      pendingWhitespace = [];
    };

    for (const part of parts) {
      if (part.type === 'image') {
        pendingImages.push(part.value);
        pendingWhitespace = []; // whitespace before this image is absorbed
      } else if (part.type === 'text' && pendingImages.length > 0 && !part.value.trim()) {
        // Whitespace-only text between images — absorb it, don't break the chain
        pendingWhitespace.push(part);
      } else {
        flushImages();
        groups.push(part);
      }
    }
    flushImages();
    return groups;
  }, [parts]);

  return (
    <div className="text-sm leading-relaxed">
      <div
        ref={collapseRef}
        className={`whitespace-pre-wrap break-words${isCollapsed ? ' relative overflow-hidden' : ''}`}
        style={isCollapsed ? { maxHeight: collapseHeight } : undefined}
      >
        {groupedParts.map((part, i) => {
          if ('images' in part && part.type === 'image-carousel') {
            return (
              <ImageCarousel
                key={`carousel-${i}`}
                images={part.images}
                onImageClick={setLightboxSrc}
              />
            );
          }
          const p = part as ContentPart;
          if (p.type === 'text') return <span key={i}>{p.value}</span>;
          if (p.type === 'url') {
            const href = p.value.match(/^https?:\/\//i) ? p.value : 'https://' + p.value;
            return (
              <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                {p.value.length > 50 ? p.value.slice(0, 50) + '...' : p.value}
              </a>
            );
          }
          if (p.type === 'image') return (
            <button
              key={i}
              type="button"
              className="block mt-2 cursor-zoom-in w-full text-left"
              onClick={() => setLightboxSrc(p.value)}
              aria-label="View full image"
            >
              <img
                src={p.value}
                alt=""
                className="max-w-full rounded-lg max-h-[400px] object-cover bg-muted hover:opacity-95 transition-opacity w-full"
                loading="lazy"
                decoding="async"
                onError={(e) => { (e.target as HTMLImageElement).closest('button')!.style.display = 'none'; }}
              />
            </button>
          );
          if (p.type === 'video') return (
            <VideoPlayer key={i} src={p.value} mime={p.mime} />
          );
          if (p.type === 'audio') return (
            <AudioPlayer key={i} src={p.value} />
          );
          if (p.type === 'embed') return (
            <VideoEmbed key={i} url={p.value} />
          );
          if (p.type === 'hashtag') return (
            <Link key={i} to={`/search?q=%23${p.value}`} className="text-primary hover:underline">#{p.value}</Link>
          );
          if (p.type === 'token') return (
            <TokenCard key={i} symbol={p.value} />
          );
          if (p.type === 'mention') return (
            <ProfileMention key={i} pubkey={p.value} />
          );
          if (p.type === 'at-mention') return (
            <AtMention key={i} username={p.value} />
          );
          if (p.type === 'nostr') return (
            <NostrMention key={i} bech32={p.value} noteId={p.noteId} />
          );
          return <span key={i}>{p.value}</span>;
        })}

        {/* Fade overlay when collapsed */}
        {isCollapsed && (
          <div className={`pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t ${fadeFromClass} to-transparent`} />
        )}
      </div>

      {/* Show more / less toggle */}
      {collapsible && overflowing && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(v => !v); }}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          {expanded ? (
            <>Show less <ChevronUp className="w-3.5 h-3.5" /></>
          ) : (
            <>Show more <ChevronDown className="w-3.5 h-3.5" /></>
          )}
        </button>
      )}

      {/* URL Preview Cards */}
      {urlsForPreview.map(url => (
        <LinkPreview key={url} url={url} />
      ))}

      {/* Image Lightbox */}
      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc}
          allImages={imageUrls}
          onClose={() => setLightboxSrc(null)}
          onNavigate={setLightboxSrc}
        />
      )}
    </div>
  );
}

// ── Image Carousel (2+ images) ───────────────────────────────────
// Horizontal swipeable carousel with dot indicators.
// Touch: swipe left/right. Desktop: arrow buttons.
// Features: loading skeletons, broken-image handling, consistent
// fixed-height container, preloading of adjacent slides.
function ImageCarousel({ images, onImageClick }: { images: string[]; onImageClick: (src: string) => void }) {
  const [current, setCurrent] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const [swiping, setSwiping] = useState(false);
  const [swipeX, setSwipeX] = useState(0);

  // Track load / error state per image
  const [loadedSet, setLoadedSet] = useState<Set<number>>(() => new Set());
  const [errorSet, setErrorSet] = useState<Set<number>>(() => new Set());

  const handleImageLoad = useCallback((idx: number) => {
    setLoadedSet(prev => { const next = new Set(prev); next.add(idx); return next; });
  }, []);

  const handleImageError = useCallback((idx: number) => {
    setErrorSet(prev => { const next = new Set(prev); next.add(idx); return next; });
    setLoadedSet(prev => { const next = new Set(prev); next.add(idx); return next; });
  }, []);

  // Valid (non-broken) image indices
  const validIndices = useMemo(() => images.map((_, i) => i).filter(i => !errorSet.has(i)), [images, errorSet]);
  const validCount = validIndices.length;

  // Clamp current to valid range
  const clampedCurrent = useMemo(() => {
    if (validCount === 0) return 0;
    if (current >= images.length) return images.length - 1;
    // If current image errored, jump to next valid
    if (errorSet.has(current)) {
      const next = validIndices.find(i => i >= current) ?? validIndices[validIndices.length - 1] ?? 0;
      return next;
    }
    return current;
  }, [current, images.length, errorSet, validIndices, validCount]);

  // Auto-correct current when it points to a broken image
  useEffect(() => {
    if (clampedCurrent !== current) setCurrent(clampedCurrent);
  }, [clampedCurrent, current]);

  const goTo = useCallback((idx: number) => {
    setCurrent(Math.max(0, Math.min(idx, images.length - 1)));
    setSwipeX(0);
  }, [images.length]);

  // Touch handlers for swipe
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
    setSwiping(true);
    setSwipeX(0);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    // Only treat as horizontal swipe if dx > dy (avoid hijacking scroll)
    if (Math.abs(dx) > dy && Math.abs(dx) > 8) {
      setSwipeX(dx);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStartRef.current) return;
    const dx = swipeX;
    const elapsed = Date.now() - touchStartRef.current.t;
    touchStartRef.current = null;
    setSwiping(false);

    // Threshold: 50px or fast flick (< 300ms and > 20px)
    const isFlick = elapsed < 300 && Math.abs(dx) > 20;
    if (dx < -50 || (isFlick && dx < 0)) {
      goTo(current + 1);
    } else if (dx > 50 || (isFlick && dx > 0)) {
      goTo(current - 1);
    } else {
      setSwipeX(0);
    }
  }, [swipeX, current, goTo]);

  // Keyboard navigation when focused
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goTo(current - 1);
    if (e.key === 'ArrowRight') goTo(current + 1);
  }, [current, goTo]);

  // If all images errored, don't render the carousel at all
  if (validCount === 0 && errorSet.size === images.length) return null;

  const translateX = swiping ? -current * 100 + (swipeX / (trackRef.current?.offsetWidth || 400)) * 100 : -current * 100;

  // Position of current among valid images (for display in counter)
  const currentValidPos = validIndices.indexOf(current);
  const displayPos = currentValidPos >= 0 ? currentValidPos + 1 : 1;

  return (
    <div
      className="relative mt-2 rounded-xl overflow-hidden bg-muted select-none group"
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="region"
      aria-label={`Image carousel, ${validCount} images`}
      aria-roledescription="carousel"
    >
      {/* Fixed-height container prevents layout shift between slides */}
      <div className="relative w-full" style={{ height: 'min(400px, 56.25vw)' }}>
        {/* Track */}
        <div
          ref={trackRef}
          className="flex absolute inset-0"
          style={{
            transform: `translateX(${translateX}%)`,
            transition: swiping ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {images.map((src, i) => {
            const isError = errorSet.has(i);
            const isLoaded = loadedSet.has(i);
            // Eagerly load current slide, adjacent slides (i-1, i+1), and first slide
            const shouldEagerLoad = i === current || i === current - 1 || i === current + 1 || i === 0;

            return (
              <div
                key={src + i}
                className="w-full h-full shrink-0 flex items-center justify-center"
                aria-hidden={i !== current}
              >
                {/* Loading skeleton */}
                {!isLoaded && !isError && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-full bg-muted animate-pulse rounded-xl" />
                  </div>
                )}

                {/* Broken image placeholder */}
                {isError && (
                  <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground/50 h-full">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    <span className="text-xs">Image unavailable</span>
                  </div>
                )}

                {/* Actual image */}
                {!isError && (
                  <button
                    type="button"
                    className="block w-full h-full cursor-zoom-in"
                    onClick={() => onImageClick(src)}
                    aria-label={`Image ${displayPos} of ${validCount}`}
                  >
                    <img
                      src={src}
                      alt=""
                      className={`w-full h-full rounded-xl object-contain bg-muted transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      loading={shouldEagerLoad ? 'eager' : 'lazy'}
                      decoding="async"
                      draggable={false}
                      onLoad={() => handleImageLoad(i)}
                      onError={() => handleImageError(i)}
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Arrow buttons (desktop — visible on hover) */}
      {current > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(current - 1); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70 z-10"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      {current < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(current + 1); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70 z-10"
          aria-label="Next image"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Counter badge */}
      {validCount > 1 && (
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-[10px] font-bold z-10">
          {displayPos} / {validCount}
        </div>
      )}

      {/* Dot indicators */}
      {validCount > 1 && validCount <= 10 && (
        <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {validIndices.map((imgIdx, dotIdx) => (
            <button
              key={imgIdx}
              onClick={(e) => { e.stopPropagation(); goTo(imgIdx); }}
              className={`rounded-full transition-all ${
                imgIdx === current
                  ? 'w-2 h-2 bg-white shadow-sm shadow-black/30'
                  : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to image ${dotIdx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Image Lightbox ────────────────────────────────────────────────
interface ImageLightboxProps {
  src: string;
  allImages: string[];
  onClose: () => void;
  onNavigate: (src: string) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function ImageLightbox({ src, allImages, onClose, onNavigate }: ImageLightboxProps) {
  const currentIndex = allImages.indexOf(src);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allImages.length - 1;

  // Zoom + pan state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const isZoomed = scale > 1.01;

  // Refs for gesture tracking
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const lastTapRef = useRef(0);
  // Pan drag
  const dragStartRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // Pinch
  const pinchStartRef = useRef<{ dist: number; scale: number; midX: number; midY: number } | null>(null);
  // Track if a touch was a tap (no movement / no pinch)
  const touchMovedRef = useRef(false);

  // Reset zoom when image changes
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  // Clamp pan so image edges don't go past the viewport
  const clampOffset = useCallback((ox: number, oy: number, s: number) => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return { x: ox, y: oy };
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const iw = img.naturalWidth || img.clientWidth;
    const ih = img.naturalHeight || img.clientHeight;
    // Rendered size at scale 1
    const renderedW = Math.min(iw, cw * 0.95);
    const renderedH = Math.min(ih, ch * 0.90);
    const maxX = Math.max(0, (renderedW * s - cw) / 2);
    const maxY = Math.max(0, (renderedH * s - ch) / 2);
    return { x: clamp(ox, -maxX, maxX), y: clamp(oy, -maxY, maxY) };
  }, []);

  // Keyboard: Escape, arrows, +/- zoom
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (isZoomed) { setScale(1); setOffset({ x: 0, y: 0 }); } else { onClose(); } }
      if (!isZoomed) {
        if (e.key === 'ArrowLeft' && hasPrev) onNavigate(allImages[currentIndex - 1]);
        if (e.key === 'ArrowRight' && hasNext) onNavigate(allImages[currentIndex + 1]);
      }
      if (e.key === '+' || e.key === '=') {
        setScale(s => clamp(s * 1.3, MIN_SCALE, MAX_SCALE));
      }
      if (e.key === '-') {
        setScale(s => { const ns = clamp(s / 1.3, MIN_SCALE, MAX_SCALE); if (ns <= 1) setOffset({ x: 0, y: 0 }); return ns; });
      }
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [src, hasPrev, hasNext, isZoomed]);

  // ── Mouse wheel zoom ──────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.85 : 1.18;
      setScale(s => {
        const ns = clamp(s * delta, MIN_SCALE, MAX_SCALE);
        if (ns <= 1) setOffset({ x: 0, y: 0 });
        return ns;
      });
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // ── Mouse drag to pan ─────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isZoomed) return;
    e.preventDefault();
    dragStartRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const clamped = clampOffset(dragStartRef.current.ox + dx, dragStartRef.current.oy + dy, scale);
    setOffset(clamped);
  };
  const handleMouseUp = () => { dragStartRef.current = null; };

  // Double-click to zoom in/out
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isZoomed) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    } else {
      setScale(3);
    }
  };

  // ── Touch events (pinch + pan + swipe + double-tap) ───────────
  const handleTouchStart = (e: React.TouchEvent) => {
    touchMovedRef.current = false;
    if (e.touches.length === 2) {
      // Pinch start
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.hypot(dx, dy);
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      pinchStartRef.current = { dist, scale, midX, midY };
      dragStartRef.current = null;
    } else if (e.touches.length === 1) {
      dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, ox: offset.x, oy: offset.y };
      pinchStartRef.current = null;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchMovedRef.current = true;
    if (e.touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.hypot(dx, dy);
      const newScale = clamp(pinchStartRef.current.scale * (dist / pinchStartRef.current.dist), MIN_SCALE, MAX_SCALE);
      setScale(newScale);
      if (newScale <= 1) setOffset({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && dragStartRef.current && isZoomed) {
      e.preventDefault();
      const dx = e.touches[0].clientX - dragStartRef.current.x;
      const dy = e.touches[0].clientY - dragStartRef.current.y;
      const clamped = clampOffset(dragStartRef.current.ox + dx, dragStartRef.current.oy + dy, scale);
      setOffset(clamped);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const wasPinch = !!pinchStartRef.current && e.changedTouches.length >= 1;
    pinchStartRef.current = null;

    if (wasPinch || e.touches.length > 0) {
      dragStartRef.current = null;
      return;
    }

    const touch = e.changedTouches[0];
    const now = Date.now();

    // Double-tap to zoom
    if (now - lastTapRef.current < 300 && !touchMovedRef.current) {
      lastTapRef.current = 0;
      if (isZoomed) {
        setScale(1);
        setOffset({ x: 0, y: 0 });
      } else {
        setScale(3);
      }
      dragStartRef.current = null;
      return;
    }
    lastTapRef.current = now;

    // Single swipe gestures — only when not zoomed
    if (!isZoomed && dragStartRef.current && !touchMovedRef.current === false) {
      const dx = touch.clientX - dragStartRef.current.x;
      const dy = touch.clientY - dragStartRef.current.y;
      // Swipe down to close
      if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { onClose(); dragStartRef.current = null; return; }
      // Swipe left/right to navigate
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0 && hasNext) onNavigate(allImages[currentIndex + 1]);
        if (dx > 0 && hasPrev) onNavigate(allImages[currentIndex - 1]);
        dragStartRef.current = null;
        return;
      }
    }

    // Tap on backdrop (not on image) to close — only when not zoomed
    if (!isZoomed && !touchMovedRef.current) {
      const img = imgRef.current;
      if (img) {
        const rect = img.getBoundingClientRect();
        const inImg = touch.clientX >= rect.left && touch.clientX <= rect.right &&
                      touch.clientY >= rect.top && touch.clientY <= rect.bottom;
        if (!inImg) onClose();
      }
    }

    dragStartRef.current = null;
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const a = document.createElement('a');
    a.href = src;
    a.download = src.split('/').pop() || 'image';
    a.target = '_blank';
    a.rel = 'noopener';
    a.click();
  };

  const navigate = (dir: 'prev' | 'next') => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    if (dir === 'prev' && hasPrev) onNavigate(allImages[currentIndex - 1]);
    if (dir === 'next' && hasNext) onNavigate(allImages[currentIndex + 1]);
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm overflow-hidden"
      onClick={!isZoomed ? onClose : undefined}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'none' }}
    >
      {/* Top controls */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10 bg-gradient-to-b from-black/70 to-transparent pointer-events-none select-none">
        <span className="text-white/60 text-sm">
          {allImages.length > 1 ? `${currentIndex + 1} / ${allImages.length}` : ''}
        </span>
        <div className="flex items-center gap-2 pointer-events-auto">
          {/* Zoom indicator */}
          {isZoomed && (
            <button
              onClick={(e) => { e.stopPropagation(); setScale(1); setOffset({ x: 0, y: 0 }); }}
              className="px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-mono transition-colors"
              title="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
          )}
          <button
            onClick={handleDownload}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image */}
      <img
        ref={imgRef}
        src={src}
        alt=""
        className="object-contain select-none"
        style={{
          maxHeight: '90dvh',
          maxWidth: '95dvw',
          transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
          transition: dragStartRef.current || pinchStartRef.current ? 'none' : 'transform 0.15s ease',
          cursor: isZoomed ? (dragStartRef.current ? 'grabbing' : 'grab') : 'zoom-in',
          willChange: 'transform',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        draggable={false}
      />

      {/* Prev arrow — hidden when zoomed */}
      {hasPrev && !isZoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate('prev'); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors backdrop-blur-sm"
          title="Previous (←)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
      )}

      {/* Next arrow — hidden when zoomed */}
      {hasNext && !isZoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate('next'); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors backdrop-blur-sm"
          title="Next (→)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      )}

      {/* Dot indicators */}
      {allImages.length > 1 && !isZoomed && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-auto">
          {allImages.map((imgSrc, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); navigate(idx < currentIndex ? 'prev' : 'next'); onNavigate(imgSrc); }}
              className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      )}

      {/* Hint text */}
      {!isZoomed && (
        <p className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/25 text-xs pointer-events-none select-none lg:hidden whitespace-nowrap">
          Pinch or double-tap to zoom
        </p>
      )}
      {isZoomed && (
        <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/25 text-xs pointer-events-none select-none whitespace-nowrap">
          Double-tap or click % to reset zoom
        </p>
      )}
    </div>
  );
}

// ── Video Player ──────────────────────────────────────────────────
// Handles codec fallbacks, error states, cross-origin audio, and layout.
// Key fix: uses preload="metadata" so audio tracks are detected before play,
// and retries without crossOrigin if CORS headers are missing.
function VideoPlayer({ src, mime }: { src: string; mime?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [triedWithoutCors, setTriedWithoutCors] = useState(false);
  // Whether the player is on (or near) screen. When it is, we pre-buffer
  // actual video data so a click on play starts almost instantly instead of
  // downloading from scratch at click-time (the old preload="metadata" only
  // fetched the file header, so the first frames arrived several seconds late).
  const [inView, setInView] = useState(false);
  // Set true once the user asks to play — after that we keep the fullest
  // preload so seeking/resuming stays snappy.
  const startedRef = useRef(false);

  // Derive MIME type hint — prefer explicit mime prop (from imeta tags),
  // then guess from extension, then default to mp4.
  //
  // NOTE: We deliberately do NOT pre-emptively reject .mov / HEVC videos.
  // iPhones upload .mov (often HEVC), which plays fine in Safari and in
  // Chrome on Apple hardware. Previously any .mov URL was forced into an
  // "Open video" link and the player was never even tried — so iPhone
  // users never saw their own videos inline. Instead we always attempt
  // playback and only fall back to the link if the <video> element fires
  // a real `error` event (see handleError below).
  //
  // For a .mov container we still hint `video/mp4` because browsers that
  // support it treat the H.264/HEVC-in-mov track the same as mp4; the
  // extra <source> fallbacks below cover the rest.
  const mimeType = mime
    ? mime
    : /\.webm(\?.*)?$/i.test(src) ? 'video/webm'
    : /\.mp4(\?.*)?$/i.test(src) ? 'video/mp4'
    : /\.mov(\?.*)?$/i.test(src) ? 'video/quicktime'
    : /\.m3u8(\?.*)?$/i.test(src) ? 'application/x-mpegURL'
    : 'video/mp4';

  const handleError = useCallback(() => {
    // If first attempt with crossOrigin fails, retry without it.
    // Some Blossom servers don't send CORS headers, which causes
    // crossOrigin="anonymous" to block the entire video (including audio).
    if (!triedWithoutCors) {
      setTriedWithoutCors(true);
      // Force the video to reload without crossOrigin
      if (videoRef.current) {
        videoRef.current.load();
      }
    } else {
      setError(true);
    }
  }, [triedWithoutCors]);

  // ── Warm the buffer while on screen ──────────────────────────────
  // Watch the container; when it enters the viewport (with a generous
  // rootMargin so buffering begins *just before* it's visible), flip
  // `inView` so the <video> can preload real data. When it scrolls far
  // away we drop back to save bandwidth — unless the user already started
  // it, in which case we leave it alone.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No IO support (or no element) — just allow preload.
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
          } else if (!startedRef.current) {
            setInView(false);
          }
        }
      },
      // Start ~1 viewport-height early so data is ready by the time the
      // user reaches (and clicks) the video.
      { rootMargin: '600px 0px', threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Once on screen, nudge the element to actually fetch data. Setting
  // preload="auto" via the attribute isn't always enough on its own after
  // the element already mounted with "metadata", so we call load() to kick
  // the fetch. Only do this the first time we come into view.
  const warmedRef = useRef(false);
  useEffect(() => {
    if (!inView || warmedRef.current) return;
    warmedRef.current = true;
    const v = videoRef.current;
    if (v && v.preload !== 'auto') {
      v.preload = 'auto';
      try { v.load(); } catch { /* ignore */ }
    }
  }, [inView]);

  // preload strategy: nothing until near-screen, then buffer real data.
  const preload = inView ? 'auto' : 'none';

  if (error) {
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 flex items-center gap-3 px-4 py-3 rounded-xl bg-muted border border-border hover:bg-muted/80 transition-colors"
      >
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Play className="w-4 h-4 text-primary translate-x-0.5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium">Open video</p>
          <p className="text-xs text-muted-foreground truncate">{src.split('/').pop()?.split('?')[0]}</p>
        </div>
      </a>
    );
  }

  return (
    <div ref={containerRef} className="mt-2 rounded-lg overflow-hidden bg-black w-full relative no-navigate" onClick={e => e.stopPropagation()}>
      <video
        ref={videoRef}
        controls
        playsInline
        preload={preload}
        crossOrigin={triedWithoutCors ? undefined : 'anonymous'}
        className="w-full max-h-[400px] block"
        style={{ display: 'block' }}
        onError={handleError}
        onLoadedMetadata={() => setLoaded(true)}
        onPlay={() => { startedRef.current = true; }}
      >
        <source src={src} type={mimeType} />
        {/* Fallback: try mp4 type hint if first source type didn't match.
            .mov containers with H.264/HEVC tracks play through the mp4
            decoder in most browsers, so this covers iPhone uploads. */}
        {mimeType !== 'video/mp4' && <source src={src} type="video/mp4" />}
        {/* Fallback: try webm type hint */}
        {mimeType !== 'video/webm' && <source src={src} type="video/webm" />}
        {/* Final catch-all: no type hint, let the browser sniff the file.
            Some browsers skip <source> tags whose type they don't
            recognise (e.g. video/quicktime), so an untyped source ensures
            the media is still attempted. */}
        <source src={src} />
      </video>
    </div>
  );
}

// ── Audio Player ─────────────────────────────────────────────────
function AudioPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play().catch(() => {}); }
  };

  const fmt = (s: number) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = x * duration;
  };

  return (
    <div className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-xl bg-muted/60 border border-border/60 no-navigate" onClick={e => e.stopPropagation()}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
        onTimeUpdate={() => { if (!dragging) setCurrentTime(audioRef.current?.currentTime ?? 0); }}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
      />

      {/* Play/pause */}
      <button
        onClick={toggle}
        className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 translate-x-0.5" />}
      </button>

      {/* Progress + time */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Scrubber */}
        <div
          className="relative h-1.5 bg-muted-foreground/20 rounded-full cursor-pointer group"
          onClick={seek}
          onMouseDown={() => setDragging(true)}
          onMouseUp={() => setDragging(false)}
        >
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-[width]"
            style={{ width: `${pct}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${pct}% - 6px)` }}
          />
        </div>
        {/* Timestamps */}
        <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono">
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>

      <Volume2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </div>
  );
}

// ── Video Embed ───────────────────────────────────────────────────

function VideoEmbed({ url }: { url: string }) {
  const embedUrl = getEmbedUrl(url);
  if (!embedUrl) {
    // Fallback to link preview if we can't embed
    return <LinkPreview url={url} />;
  }

  return (
    <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-black no-navigate" onClick={e => e.stopPropagation()}>
      <iframe
        src={embedUrl}
        className="w-full h-full border-0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        loading="lazy"
        title="Video"
      />
    </div>
  );
}

function getEmbedUrl(url: string): string | null {
  // YouTube
  let match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  if (match) return `https://www.youtube-nocookie.com/embed/${match[1]}`;

  // Rumble
  match = url.match(/rumble\.com\/([a-zA-Z0-9-]+)\.html/);
  if (match) return `https://rumble.com/embed/${match[1]}/`;

  // Odysee
  match = url.match(/odysee\.com\/(@[^/]+\/[^?]+)/);
  if (match) return `https://odysee.com/$/embed/${match[1]}`;

  // Vimeo
  match = url.match(/vimeo\.com\/(\d+)/);
  if (match) return `https://player.vimeo.com/video/${match[1]}`;

  // Streamable
  match = url.match(/streamable\.com\/([a-zA-Z0-9]+)/);
  if (match) return `https://streamable.com/e/${match[1]}`;

  // Twitch clips
  match = url.match(/twitch\.tv\/\w+\/clip\/([a-zA-Z0-9-]+)/);
  if (match) return `https://clips.twitch.tv/embed?clip=${match[1]}&parent=${window.location.hostname}`;

  // Twitch channel
  match = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)$/);
  if (match) return `https://player.twitch.tv/?channel=${match[1]}&parent=${window.location.hostname}`;

  return null;
}

// ── Link Preview Card ─────────────────────────────────────────────
const previewCache: Map<string, { title?: string; description?: string; image?: string; siteName?: string } | null> = new Map();

function LinkPreview({ url }: { url: string }) {
  const [meta, setMeta] = useState<{ title?: string; description?: string; image?: string; siteName?: string } | null>(
    previewCache.get(url) ?? null
  );
  const [loaded, setLoaded] = useState(previewCache.has(url));

  useEffect(() => {
    if (previewCache.has(url)) {
      setMeta(previewCache.get(url)!);
      setLoaded(true);
      return;
    }

    let cancelled = false;

    // Fetch the page HTML and parse OG tags
    (async () => {
      try {
        const proxyUrl = `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;
        const res = await fetch(proxyUrl, { signal: AbortSignal.timeout(6000) });
        if (!res.ok) throw new Error('fetch failed');
        const html = await res.text();

        // Parse OG tags from HTML
        const getTag = (prop: string): string | undefined => {
          const match = html.match(new RegExp(`<meta[^>]+(?:property|name)=["']${prop}["'][^>]+content=["']([^"']+)["']`, 'i'))
            || html.match(new RegExp(`<meta[^>]+content=["']([^"']+)["'][^>]+(?:property|name)=["']${prop}["']`, 'i'));
          return match?.[1];
        };

        const title = getTag('og:title') || getTag('twitter:title') || html.match(/<title[^>]*>([^<]+)/i)?.[1];
        const description = getTag('og:description') || getTag('twitter:description') || getTag('description');
        const image = getTag('og:image') || getTag('twitter:image');
        const siteName = getTag('og:site_name');

        if (!cancelled && (title || description || image)) {
          const data = { title: title?.slice(0, 120), description: description?.slice(0, 200), image, siteName };
          previewCache.set(url, data);
          setMeta(data);
        } else {
          previewCache.set(url, null);
        }
      } catch {
        previewCache.set(url, null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  if (!loaded || !meta) return null;

  const domain = (() => { try { return new URL(url).hostname; } catch { return ''; } })();

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 block rounded-lg border border-border overflow-hidden hover:bg-muted/30 transition-colors"
    >
      {meta.image && (
        <div className="aspect-[2/1] max-h-48 overflow-hidden bg-muted">
          <img
            src={meta.image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
          />
        </div>
      )}
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-0.5">
          <Globe className="w-3 h-3" />
          <span>{meta.siteName || domain}</span>
        </div>
        {meta.title && (
          <p className="text-xs font-semibold line-clamp-2 leading-snug">{meta.title}</p>
        )}
        {meta.description && (
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{meta.description}</p>
        )}
      </div>
    </a>
  );
}

// ── Content Parser ────────────────────────────────────────────────
interface ContentPart {
  type: 'text' | 'url' | 'image' | 'video' | 'audio' | 'embed' | 'hashtag' | 'mention' | 'nostr' | 'link' | 'at-mention' | 'token';
  value: string;   // URL for links, text for display; ticker symbol for 'token'
  display?: string; // display text for 'link' type
  noteId?: string; // for nevent/note links
  mime?: string; // explicit mime type from imeta tags
}

// ── Profile Mention (hex pubkey → display name) ──────────────────
function ProfileMention({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  const name = profile?.name || profile?.display_name || pubkey.slice(0, 8) + '...';
  return <Link to={`/profile/${pubkey}`} className="text-primary hover:underline font-medium">@{name}</Link>;
}

// ── At Mention (raw @username → try to resolve from profile cache) ──
function AtMention({ username }: { username: string }) {
  const result = useMemo(() => findProfileByName(username), [username]);
  if (result) {
    return <Link to={`/profile/${result.pubkey}`} className="text-primary hover:underline font-medium">@{username}</Link>;
  }
  // Not found — render as plain text with a subtle style so user sees it's not a real tag
  return <span className="text-muted-foreground">@{username}</span>;
}

// ── Nostr Mention (bech32 → resolved link) ───────────────────────
function NostrMention({ bech32, noteId }: { bech32: string; noteId?: string }) {
  // If it's a note/nevent reference, embed the note inline.
  if (noteId) {
    return <EmbeddedNote noteId={noteId} />;
  }

  // Try to decode to get a pubkey for profile mentions
  try {
    const decoded = nip19.decode(bech32);
    if (decoded.type === 'npub') {
      return <ProfileMention pubkey={decoded.data as string} />;
    }
    if (decoded.type === 'nprofile') {
      return <ProfileMention pubkey={(decoded.data as any).pubkey} />;
    }
    if (decoded.type === 'note') {
      return <EmbeddedNote noteId={decoded.data as string} />;
    }
    if (decoded.type === 'nevent') {
      return <EmbeddedNote noteId={(decoded.data as any).id} />;
    }
  } catch {}

  // Fallback: show truncated bech32
  return <span className="text-primary font-mono text-xs">{bech32.slice(0, 12)}...</span>;
}

// ── Embedded Note Card ────────────────────────────────────────────
// Renders a referenced note inline as a compact card (author + preview),
// linking through to the full thread. Used for nostr: note/nevent
// mentions AND for internal /note/<id> links pasted as plain URLs.
// While the note is loading (or if it can't be found) we fall back to a
// small "note" pill so the reference never renders as blank.
function EmbeddedNote({ noteId }: { noteId: string }) {
  // Query across all read relays — a referenced note frequently lives on
  // relays outside the default 3-relay subset, which is why some embeds
  // resolved and others fell back to a "note" pill / "can't find post".
  const { event, loading } = useNostrEvent(noteId, true);
  const { profile } = useNostrProfile(event?.pubkey);

  if (!event) {
    return (
      <Link
        to={`/note/${noteId}`}
        className="inline-flex items-center gap-1 text-primary hover:underline font-medium border border-primary/20 rounded px-1.5 py-0.5 text-xs bg-primary/5 align-middle"
      >
        📝 {loading ? 'loading note…' : 'note'}
      </Link>
    );
  }

  const name = profile?.display_name || profile?.name || event.pubkey.slice(0, 8);
  const preview = event.content.trim().slice(0, 280);

  return (
    <Link
      to={`/note/${noteId}`}
      className="mt-2 mb-1 block rounded-lg border border-border bg-muted/30 p-3 hover:bg-muted/50 transition-colors no-navigate"
    >
      <div className="flex items-center gap-1.5 mb-1">
        {profile?.picture && (
          <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" loading="lazy" />
        )}
        <span className="text-[11px] font-medium">{name}</span>
      </div>
      {preview ? (
        <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap break-words">{preview}</p>
      ) : (
        <p className="text-xs text-muted-foreground italic">View note</p>
      )}
    </Link>
  );
}

const IMAGE_EXTS = /\.(jpg|jpeg|png|gif|webp|svg|avif)(\?.*)?$/i;
const VIDEO_EXTS = /\.(mp4|webm|mov|avi|m3u8)(\?.*)?$/i;
const AUDIO_EXTS = /\.(mp3|ogg|wav|aac|flac|m4a|opus)(\?.*)?$/i;

// ── Internal note-link detection ─────────────────────────────────
// Recognises links that point at a note *on this app* and resolves them
// to a raw hex event id so we can embed the note inline instead of
// rendering a plain (and effectively dead) external link.
//
// Handles:
//   https://bchnostr.com/note/<64-hex>
//   https://bchnostr.com/note/note1...   (nip19 note)
//   https://bchnostr.com/note/nevent1... (nip19 nevent)
//   /note/<...>                          (relative, same forms)
// The host is intentionally permissive (any *.bchnostr.com or a bare
// /note/ path) so links copied from staging / preview domains still embed.
function extractInternalNoteId(rawUrl: string): string | undefined {
  const m = rawUrl.match(/(?:^|\/\/[^/]*bchnostr\.com)\/note\/([A-Za-z0-9]+)/i)
    || rawUrl.match(/^\/note\/([A-Za-z0-9]+)/i);
  const token = m?.[1];
  if (!token) return undefined;

  // Bare 64-char hex event id
  if (/^[0-9a-f]{64}$/i.test(token)) return token.toLowerCase();

  // nip19 note / nevent identifier
  if (/^(note1|nevent1)[a-z0-9]+$/i.test(token)) {
    try {
      const decoded = nip19.decode(token);
      if (decoded.type === 'note') return decoded.data as string;
      if (decoded.type === 'nevent') return (decoded.data as { id: string }).id;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

// Detect embeddable video platform URLs
function isEmbeddableVideo(url: string): boolean {
  return /(?:youtube\.com\/watch|youtu\.be\/|youtube\.com\/shorts\/|rumble\.com\/|odysee\.com\/|twitch\.tv\/|vimeo\.com\/|streamable\.com\/)/i.test(url);
}
// Match full URLs (https://...) OR bare domain URLs (example.com/path, www.example.com)
// The bare domain pattern requires a known TLD or www prefix to avoid false positives.
// IMPORTANT: all alternatives must be inside ONE capture group for split() to work.
const KNOWN_TLDS = 'com|org|net|io|co|app|dev|xyz|me|info|biz|cc|tv|us|uk|ca|de|fr|au|in|ch|nl|be|at|se|fi|no|dk|it|es|pt|br|jp|kr|ru|cn|tw|nz|za|mx|cash|money|exchange|social|club|online|store|shop|site|tech|ai|gg|to|im|so|ly|land|world|space|live|pro|page|link|fun|lol|wtf|eth|bitcoin';
// TLDs are alternated in the regex, and JS alternation matches the FIRST
// option that fits — not the longest. Since `ca` appears before `cash`, a
// bare domain like "FundMe.cash" would match only "FundMe.ca", leaving "sh"
// as unlinkified stray text. Sorting the alternatives longest-first ensures
// `cash` is tried before `ca`. The trailing (?![a-zA-Z0-9]) lookahead is a
// second guard: it forbids the TLD from being immediately followed by more
// domain characters, so a TLD is only accepted at a real word boundary.
const KNOWN_TLDS_SORTED = KNOWN_TLDS.split('|').sort((a, b) => b.length - a.length).join('|');
const URL_REGEX = new RegExp(
  '(' +
    'https?:\\/\\/[^\\s<]+' +                                           // https://...
    '|' +
    'www\\.[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\\.[a-zA-Z]{2,}(?:\\/[^\\s<]*)?' + // www.example.com/path
    '|' +
    '[a-zA-Z0-9][a-zA-Z0-9-]*\\.(?:' + KNOWN_TLDS_SORTED + ')(?![a-zA-Z0-9])(?:\\/[^\\s<]*)?' + // example.com/path
  ')',
  'gi'
);

function parseContent(content: string, tags: string[][]): ContentPart[] {
  const parts: ContentPart[] = [];
  const urlParts = content.split(URL_REGEX);

  // Build a map of URL -> mime type from imeta tags so we can identify
  // Blossom URLs (bare sha256 hashes) that have no file extension
  const imetaMimeMap = new Map<string, string>();
  for (const tag of tags) {
    if (tag[0] !== 'imeta') continue;
    let url = '';
    let mime = '';
    for (const v of tag) {
      if (v.startsWith('url ')) url = v.slice(4);
      if (v.startsWith('m ')) mime = v.slice(2);
    }
    if (url && mime) imetaMimeMap.set(url, mime);
  }

  // split with a capture group produces alternating: [text, url, text, url, ...]
  // Odd indices (1, 3, 5...) are URL matches, even indices (0, 2, 4...) are text
  for (let i = 0; i < urlParts.length; i++) {
    const segment = urlParts[i];
    if (!segment) continue;

    const isUrlSegment = i % 2 === 1;

    if (isUrlSegment) {
      // Normalize: ensure URL has protocol for media src/href usage
      const fullUrl = segment.match(/^https?:\/\//i) ? segment : 'https://' + segment;

      // Check imeta tags first for explicit mime type (handles Blossom URLs)
      const imetaMime = imetaMimeMap.get(segment) || imetaMimeMap.get(fullUrl);

      if (imetaMime?.startsWith('video/')) {
        parts.push({ type: 'video', value: fullUrl, mime: imetaMime });
      } else if (imetaMime?.startsWith('audio/')) {
        parts.push({ type: 'audio', value: fullUrl });
      } else if (imetaMime?.startsWith('image/')) {
        parts.push({ type: 'image', value: fullUrl });
      } else if (IMAGE_EXTS.test(segment)) {
        parts.push({ type: 'image', value: fullUrl });
      } else if (VIDEO_EXTS.test(segment)) {
        parts.push({ type: 'video', value: fullUrl });
      } else if (AUDIO_EXTS.test(segment)) {
        parts.push({ type: 'audio', value: fullUrl });
      } else if (isEmbeddableVideo(segment)) {
        parts.push({ type: 'embed', value: fullUrl });
      } else {
        // Internal note links (e.g. https://bchnostr.com/note/<id>) should
        // render as an embedded note card, not a dead-looking plain link.
        // This is what users expect when they paste a link to one of their
        // own posts into a comment / community submission.
        const internalNoteId = extractInternalNoteId(segment);
        if (internalNoteId) {
          parts.push({ type: 'nostr', value: '', noteId: internalNoteId });
        } else {
          // Keep original (display) value — href will be normalized in the renderer
          parts.push({ type: 'url', value: segment });
        }
      }
    } else {
      let lastIndex = 0;
      // Detect: #hashtags, nostr: bech32 mentions, #[idx] legacy tag refs, and @username raw mentions
      const combinedRegex = /(?:#(\w{2,})|nostr:(npub1[a-z0-9]+|nprofile1[a-z0-9]+|note1[a-z0-9]+|nevent1[a-z0-9]+|naddr1[a-z0-9]+)|#\[(\d+)\]|(?:^|[\s(])(@[a-zA-Z0-9_]{1,64})|(?:^|[\s(])\$([A-Za-z][A-Za-z0-9]{1,9})\b)/g;
      let match;

      while ((match = combinedRegex.exec(segment)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ type: 'text', value: segment.slice(lastIndex, match.index) });
        }
        if (match[1]) {
          parts.push({ type: 'hashtag', value: match[1] });
        } else if (match[2]) {
          // Decode nostr: mentions to get the actual pubkey/noteId
          const bech32 = match[2];
          let noteId: string | undefined;
          try {
            const decoded = nip19.decode(bech32);
            if (decoded.type === 'npub') {
              parts.push({ type: 'mention', value: decoded.data as string });
            } else if (decoded.type === 'nprofile') {
              parts.push({ type: 'mention', value: (decoded.data as any).pubkey });
            } else if (decoded.type === 'note') {
              parts.push({ type: 'nostr', value: bech32, noteId: decoded.data as string });
            } else if (decoded.type === 'nevent') {
              parts.push({ type: 'nostr', value: bech32, noteId: (decoded.data as any).id });
            } else {
              parts.push({ type: 'nostr', value: bech32 });
            }
          } catch {
            parts.push({ type: 'nostr', value: bech32 });
          }
        } else if (match[3]) {
          const idx = parseInt(match[3]);
          const tag = tags[idx];
          if (tag && tag[0] === 'p') {
            parts.push({ type: 'mention', value: tag[1] });
          } else if (tag && tag[0] === 'e') {
            parts.push({ type: 'nostr', value: '', noteId: tag[1] });
          } else {
            parts.push({ type: 'text', value: match[0] });
          }
        } else if (match[4]) {
          // Raw @username mention — try to resolve it
          // match[4] includes the @ prefix, strip it for the value
          const rawAt = match[4]; // e.g. "@username"
          const username = rawAt.startsWith('@') ? rawAt.slice(1) : rawAt;
          // If the match included a leading space (from the [\s(] group), push it as text
          const fullMatchStr = match[0];
          if (fullMatchStr.length > rawAt.length) {
            parts.push({ type: 'text', value: fullMatchStr.slice(0, fullMatchStr.length - rawAt.length) });
          }
          parts.push({ type: 'at-mention', value: username });
        } else if (match[5]) {
          // $TICKER cashtoken mention. The match may include a leading space
          // from the (?:^|[\s(]) prefix — push that back as text first.
          const ticker = match[5]; // without the $ prefix
          const matched = match[0];
          const prefixLen = matched.length - (ticker.length + 1); // +1 for '$'
          if (prefixLen > 0) {
            parts.push({ type: 'text', value: matched.slice(0, prefixLen) });
          }
          parts.push({ type: 'token', value: ticker });
        }
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < segment.length) {
        parts.push({ type: 'text', value: segment.slice(lastIndex) });
      } else if (lastIndex === 0) {
        parts.push({ type: 'text', value: segment });
      }
    }
  }

  return parts;
}

